/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchFilter from './SearchFilter';
import { Pagination, Box, Typography, Modal, IconButton } from '@mui/material';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import {
    ActivityLogsIcons,
    DesktopLoginIcon,
    ExportIcon,
    FlagIcons,
    GoldIconsUtm,
    HeartIconsUtm,
    LeftArrowIcons,
    LuxuryIconsUtm,
    MobileLoginIcon,
    VerifiedIcons,
} from '../../icons';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UserTableSkeleton from '../user-management/UserTableSkeleton';
import { fetchUsersList } from '../../api/user';
import { getAllUtmList } from '../../api/utm';
import { normalizeCampaignRow, getRowsFromResponse } from '../../containers/campaign-container/utils';
import { initColumns, toggleColumn } from '../../containers/app-container/reducer/visibleColumnsSlice';
import { timeAgo, formatDate, formatDuration } from '../../utils/dateTime';
import ActivityLogs from '../user-management/ActivityLogs';
import SuspendCard from '../user-management/SuspendCard';
import UserProfileMainPage from '../user-profile-main/UserProfileMainPage';
import { statusLabel } from '../../utils/memberStatus';
import { useNotification } from '../../hooks/useNotification';
import { downloadCsv } from '../../utils/fileUtils';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    width: '95%',
    margin: '0 auto',
    bgcolor: 'background.paper',
    borderRadius: '20px',
    boxShadow: 24,
    p: 4,
    maxHeight: '80vh',
    overflow: 'auto',
};

const styleUser = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1080,
    width: '95%',
    margin: '0 auto',
    bgcolor: 'background.paper',
    borderRadius: '20px',
    boxShadow: 24,
    p: 4,
    maxHeight: '80vh',
    overflow: 'auto',
};

const styleProfile = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1300,
    width: '95%',
    margin: '0 auto',
    bgcolor: 'background.paper',
    borderRadius: '20px',
    boxShadow: 24,
    p: '0 30px 30px 30px',
    maxHeight: '80vh',
    overflow: 'auto',
};

const visibleColumnKey = [
    'name',
    'email',
    'age',
    'gender',
    'location',
    'status',
    'date',
    'session',
    'intrested',
    'profileview',
    'conversation',
    'duration',
    'appinstalled',
    'verification',
    'accountstatus',
    'reason',
    'lastlogin',
    'flagreport',
    'blockedreport',
    'activitylogs',
    'action',
];

const statsTypeMap = {
    signups: 'campaign_signups',
    'profile-completed': 'complete_signup',
    'ghost-users': 'campaign_ghost_users',
    'web-users': 'web_users',
    'app-users': 'app_users',
    'ghost-web-users': 'ghost_web_users',
    'ghost-app-users': 'ghost_app_users',
    subscribed: 'total_subscription',
    subscriptions: 'total_subscription',
};

// Marks the getUsers call as originating from the campaign user list so the
// backend applies the app/web (user_tokens) split. Other callers omit it and
// fall back to default behaviour.
const CAMPAIGN_LIST_SOURCE = 'user-list-campaign';

const getFullName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.name ||
    user?.userName ||
    '-';

const getLocation = (user) => [user?.city, user?.state, user?.country].filter(Boolean).join(', ') || '-';

const getSubscriptionLabel = (user) => {
    if (user?.isSubscribed && user?.subscriptionType) return user.subscriptionType;
    if (user?.isSubscribed) return 'Subscribed';
    return 'Free';
};

const getVerificationLabel = (user) => {
    if (user?.selfieVerificationStatus === 'Approved') return 'Verified';
    if (user?.selfieVerificationStatus === 'Unverified') return 'Pending';
    if (user?.selfieVerificationStatus) return 'Mismatch';
    return 'Pending';
};

// The server decides the status (getUsers `statusBucket`); this only names it.
const getUserStatus = (user) => (user?.statusBucket ? statusLabel(user.statusBucket) : legacyUserStatus(user));

// Rollout fallback for a server that does not send statusBucket yet — remove once it does.
const legacyUserStatus = (user) => {
    if (user?.suspend) return 'Suspended';
    if (user?.isInvisible && user?.hideByAdmin) return 'Hidden by Admin';
    if (user?.active === false) return 'Deactivated';
    if ((user?.profileCompletion ?? 0) < 100) return 'Incomplete Profile';

    const notVerified = user?.selfieVerificationStatus !== 'Approved' || !user?.profilePicture;
    if (notVerified) return 'Inactive';
    if (user?.isInvisible) return 'Hidden';
    return 'Active';
};

const normalizeUserForTable = (user) => ({
    ...user,
    image: user?.image || user?.profilePicture || '/assets/images/user-grid/user-grid-img1.png',
    name: getFullName(user),
    age: user?.age ?? '-',
    gender: user?.gender || '-',
    location: getLocation(user),
    status: getSubscriptionLabel(user),
    date: user?.createdAt ? formatDate(user.createdAt) : '-',
    session: user?.sessionCount ?? 0,
    intrested: user?.interestedCount ?? 0,
    profileview: user?.viewCount ?? 0,
    conversation: user?.conversationCount ?? 0,
    duration: user?.totalDuration || formatDuration(user?.durationMin || 0),
    appinstalled: user?.appInstalled ? 'Yes' : 'No',
    verification: getVerificationLabel(user),
    accountstatus: getUserStatus(user),
    reason: user?.suspend ? (user?.suspendReason || '-') : (user?.hideReason || '-'),
    lastlogin: user?.isOnLine ? 'Online' : user?.lastLogin ? timeAgo(user.lastLogin) : '0',
    isOnline: Boolean(user?.isOnLine),
    loginDevices: user?.appInstalled ? ['mobile'] : ['desktop'],
    flagreport: user?.reportCount ?? 0,
    blockedreport: user?.blockCount ?? 0,
});

const UserListCampaignsPage = ({ type: propType, utmId: propUtmId, onClose, embedded = false }) => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const type = propType || params?.type;
    const utmId = propUtmId || params?.utmId;
    const dispatch = useDispatch();
    const { showErrorNotification } = useNotification();

    const fullFilter = useSelector((state) => state.filter);
    const selectedCountry = useSelector((state) => state.filter.country);
    const visibleColumns = useSelector((state) => state.visibleColumns.pages.campaignUsers || {});
    const [, setCampaign] = useState(null);
    const [userData, setUserData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [genderFilter, setGenderFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [campaignLoading, setCampaignLoading] = useState(false);
    const [hasLoadedUsers, setHasLoadedUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [openLogs, setOpenLogs] = useState(false);
    const [openSuspend, setOpenSuspend] = useState(false);
    const [openProfile, setOpenProfile] = useState(false);
    const tableContainerRef = useRef(null);
    const scrollThumbRef = useRef(null);
    // Tracks the filter/search/type signature the page count was last set for,
    // so paging through the same result set doesn't refresh totalPages (the
    // backend recomputes the total live, which would otherwise jiggle it ±1).
    const lastTotalsKeyRef = useRef(null);
    const [scrollState, setScrollState] = useState({
        scrollLeft: 0,
        scrollWidth: 0,
        clientWidth: 0,
    });

    const requestFilter = useMemo(
        () => ({
            country: fullFilter?.country,
            state: fullFilter?.state,
            city: fullFilter?.city,
            dateRange: fullFilter?.dateRange,
            gender: fullFilter?.gender,
            activeVerificationTab: fullFilter?.activeVerificationTab,
            sortBy: fullFilter?.sortBy,
            sortOrder: fullFilter?.sortOrder,
            profileCompilation: fullFilter?.profileCompilation,
            statsTitle: fullFilter?.statsTitle,
        }),
        [
            fullFilter?.country,
            fullFilter?.state,
            fullFilter?.city,
            fullFilter?.dateRange,
            fullFilter?.gender,
            fullFilter?.activeVerificationTab,
            fullFilter?.sortBy,
            fullFilter?.sortOrder,
            fullFilter?.profileCompilation,
            fullFilter?.statsTitle,
        ]
    );

    const thumbWidth = useMemo(() => {
        if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
            const ratio = scrollState.clientWidth / scrollState.scrollWidth;
            return Math.max(ratio * scrollState.clientWidth, 50);
        }
        return 100;
    }, [scrollState]);

    const thumbPosition = useMemo(() => {
        if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
            const ratio = scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth);
            return ratio * (scrollState.clientWidth - thumbWidth);
        }
        return 0;
    }, [scrollState, thumbWidth]);

    const updateScrollState = () => {
        if (tableContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
            setScrollState({
                scrollLeft,
                scrollWidth,
                clientWidth,
            });
        }
    };

    const handleTableScroll = () => {
        updateScrollState();
    };

    const handleStickyScroll = (e) => {
        if (!tableContainerRef.current) return;

        const track = e.currentTarget;
        const trackRect = track.getBoundingClientRect();
        const clickX = e.clientX - trackRect.left;
        const thumbCenter = thumbWidth / 2;
        const newScrollLeft =
            ((clickX - thumbCenter) / (trackRect.width - thumbWidth)) *
            (scrollState.scrollWidth - scrollState.clientWidth);

        tableContainerRef.current.scrollLeft = newScrollLeft;
    };

    const handleThumbDrag = (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startScrollLeft = scrollState.scrollLeft;

        const handleMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const trackWidth = scrollState.clientWidth - thumbWidth;
            const scrollableWidth = scrollState.scrollWidth - scrollState.clientWidth;

            if (trackWidth > 0) {
                const newScrollLeft = startScrollLeft + (deltaX / trackWidth) * scrollableWidth;
                if (tableContainerRef.current) {
                    tableContainerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, scrollableWidth));
                }
            }
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };


    const fetchData = useCallback(async () => {
        if (!utmId) {
            setUserData([]);
            setTotalPages(0);
            setHasLoadedUsers(true);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const data = await fetchUsersList({
                filter: {
                    ...requestFilter,
                    gender: genderFilter || requestFilter?.gender,
                    requestType: 'user_management',
                    statsType: statsTypeMap[type] || type || null,
                    source: CAMPAIGN_LIST_SOURCE,
                    utmId: utmId || null,
                    utm_payload: {
                        utm_source: null,
                        utm_medium: null,
                        utm_campaign: null,
                        utm_content: null,
                        utm_term: null,
                    },
                },
                setUserData: (users) => setUserData((users || []).map(normalizeUserForTable)),
                page: currentPage,
                limit: itemsPerPage,
                skip: 0,
                isBot: false,
                searchTerm: debouncedSearchTerm || undefined,
            });

            // Refresh the page count only when the result set (filters/search/
            // type/utm) changes — not on every page click — so it stays stable
            // while browsing.
            const totalsKey = JSON.stringify({ requestFilter, genderFilter, debouncedSearchTerm, type, utmId, itemsPerPage });
            if (lastTotalsKeyRef.current !== totalsKey) {
                lastTotalsKeyRef.current = totalsKey;
                setTotalPages(data?.totalPages || 0);
            }
            setHasLoadedUsers(true);
        } catch (err) {
            console.error('Error while loading campaign user data', err);
            showErrorNotification(
                err?.response?.data?.message || err?.message || 'Failed to load campaign users'
            );
            setUserData([]);
            setTotalPages(0);
            lastTotalsKeyRef.current = null; // allow totals to refresh on retry
            setHasLoadedUsers(true);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, debouncedSearchTerm, genderFilter, requestFilter, type, showErrorNotification, utmId]);


    useEffect(() => {
        dispatch(
            initColumns({
                pageKey: 'campaignUsers',
                columns: visibleColumnKey,
            })
        );
    }, [dispatch]);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);
    useEffect(() => {
        setCurrentPage(1);
    }, [
        debouncedSearchTerm,
        genderFilter,
        selectedCountry,
        fullFilter?.dateRange?.startDate,
        fullFilter?.dateRange?.endDate,
        type,
        utmId,
    ]);
    useEffect(() => {
        updateScrollState();
        window.addEventListener('resize', updateScrollState);

        return () => {
            window.removeEventListener('resize', updateScrollState);
        };
    }, [userData, visibleColumns]);
    useEffect(() => {
        let isMounted = true;

        const loadCampaign = async () => {
            try {
                setCampaignLoading(true);
                const response = await getAllUtmList();
                const campaigns = getRowsFromResponse(response).map(normalizeCampaignRow);
                const selectedCampaign = campaigns.find((item) => {
                    const candidateIds = [item?.id, item?._id, item?.campaignId, item?.utmId].filter(Boolean);
                    return candidateIds.some((value) => String(value) === String(utmId));
                }) || null;

                if (!isMounted) return;

                setCampaign((prev) => {
                    const prevId = prev?.id || prev?._id || prev?.campaignId || prev?.utmId || null;
                    const nextId =
                        selectedCampaign?.id ||
                        selectedCampaign?._id ||
                        selectedCampaign?.campaignId ||
                        selectedCampaign?.utmId ||
                        null;

                    if (
                        prevId === nextId &&
                        prev?.campaignSource === selectedCampaign?.campaignSource &&
                        prev?.campaignName === selectedCampaign?.campaignName &&
                        prev?.isActive === selectedCampaign?.isActive
                    ) {
                        return prev;
                    }

                    return selectedCampaign;
                });
            } catch (error) {
                if (isMounted) {
                    showErrorNotification(
                        error?.response?.data?.message || error?.message || 'Failed to load campaign details'
                    );
                }
            } finally {
                if (isMounted) {
                    setCampaignLoading(false);
                }
            }
        };

        if (utmId) {
            loadCampaign();
        } else {
            setCampaign(null);
            setCampaignLoading(false);
        }

        return () => {
            isMounted = false;
        };
    }, [utmId, showErrorNotification]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleColumnToggle = (key) => {
        dispatch(
            toggleColumn({
                pageKey: 'campaignUsers',
                column: key,
            })
        );
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const handleOpenLogs = (user) => {
        setSelectedUser(user);
        setOpenLogs(true);
    };

    const handleOpenSuspend = (user) => {
        setSelectedUser(user);
        setOpenSuspend(true);
    };

    const handleOpenProfile = (user) => {
        setSelectedUser(user);
        setOpenProfile(true);
    };

    const handleBack = () => {
        if (embedded) {
            onClose?.();
            return;
        }

        navigate(`/utm-campaigns${location.search || ''}`);
    };

    const exportHeaders = [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'age', label: 'Age' },
        { key: 'gender', label: 'Gender' },
        { key: 'location', label: 'Location' },
        { key: 'status', label: 'Subscription Status' },
        { key: 'date', label: 'Registration Date' },
        { key: 'session', label: 'Session' },
        { key: 'intrested', label: 'Interested' },
        { key: 'profileview', label: 'Profile View' },
        { key: 'conversation', label: 'Conversation' },
        { key: 'duration', label: 'Duration' },
        { key: 'appinstalled', label: 'App Installed' },
        { key: 'verification', label: 'Verification' },
        { key: 'accountstatus', label: 'Account Status' },
        { key: 'reason', label: 'Reason' },
        { key: 'lastlogin', label: 'Last Login' },
        { key: 'flagreport', label: 'Flag Report By Users' },
        { key: 'blockedreport', label: 'Blocked By Users' },
    ];

    const handleExport = async () => {
        try {
            setLoading(true);
            const response = await fetchUsersList({
                filter: {
                    ...requestFilter,
                    gender: genderFilter || requestFilter?.gender,
                    requestType: 'user_management',
                    statsType: statsTypeMap[type] || type || null,
                    source: CAMPAIGN_LIST_SOURCE,
                    utmId: utmId || null,
                    utm_payload: {
                        utm_source: null,
                        utm_medium: null,
                        utm_campaign: null,
                        utm_content: null,
                        utm_term: null,
                    },
                },
                setUserData: () => { },
                page: 1,
                limit: 5000,
                skip: 0,
                isBot: false,
                searchTerm: debouncedSearchTerm || undefined,
            });

            const rows = (response?.users || []).map(normalizeUserForTable);
            const headers = exportHeaders.filter((header) => visibleColumns[header.key]);
            downloadCsv({
                fileName: 'campaign-users.csv',
                headers,
                rows,
            });
        } catch (error) {
            showErrorNotification(
                error?.response?.data?.message || error?.message || 'Failed to export campaign users'
            );
        } finally {
            setLoading(false);
        }
    };

    const columnHeaders = [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'age', label: 'Age' },
        { key: 'gender', label: 'Gender' },
        { key: 'location', label: 'Location' },
        { key: 'status', label: 'Subscription Status' },
        { key: 'date', label: 'Registration Date' },
        { key: 'session', label: 'Session' },
        { key: 'intrested', label: 'Intrested' },
        { key: 'profileview', label: 'Profile View' },
        { key: 'conversation', label: 'Conversation' },
        { key: 'duration', label: 'Duration' },
        { key: 'appinstalled', label: 'App Installed' },
        { key: 'verification', label: 'Verification' },
        { key: 'accountstatus', label: 'Account Status' },
        { key: 'reason', label: 'Reason' },
        { key: 'lastlogin', label: 'Last Login' },
        { key: 'flagreport', label: 'Flag Report By Users' },
        { key: 'blockedreport', label: 'Blocked By Users' },
        { key: 'activitylogs', label: 'Activity Logs' },
        { key: 'action', label: 'Action' },
    ];

    const baseStatusStyle = {
        padding: '4px 16px',
        borderRadius: '20px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        textTransform: 'uppercase',
        fontSize: '13px',
        fontWeight: '400',
    };

    const getStatusConfig = (status) => {
        switch (String(status || '').toLowerCase()) {
            case 'gold':
                return {
                    background: 'linear-gradient(180deg, #916008 0%, #5E3E05 50%, #2B1C02 100%)',
                    color: '#BF8500',
                    icon: <GoldIconsUtm />,
                };
            case 'luxury':
            case 'subscribed':
                return {
                    background: 'linear-gradient(180deg, #EA4335 0%, #B02521 95.31%)',
                    color: '#fff',
                    icon: <LuxuryIconsUtm />,
                };
            case 'free':
            default:
                return {
                    background: 'transparent',
                    color: '#000',
                    icon: null,
                };
        }
    };

    const baseAppStyle = {
        padding: '4px 16px',
        borderRadius: '20px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        fontWeight: '400',
    };

    const getAppConfig = (appinstalled) => {
        switch (String(appinstalled || '').toLowerCase()) {
            case 'yes':
                return {
                    background: '#91600857',
                    color: '#916008',
                };
            case 'no':
                return {
                    background: '#E8E8E8',
                    color: '#848484',
                };
            default:
                return {
                    background: 'transparent',
                    color: '#000',
                };
        }
    };

    const baseVerificationStyle = {
        padding: '4px 16px',
        borderRadius: '20px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        fontWeight: '400',
    };

    const getVerificationConfig = (verification) => {
        switch (String(verification || '').toLowerCase()) {
            case 'verified':
                return {
                    color: '#916008',
                    icon: <VerifiedIcons />,
                };
            case 'pending':
                return {
                    color: '#757575',
                    icon: '',
                };
            default:
                return {
                    color: '#000',
                    icon: '',
                };
        }
    };

    const baseAccountStatusStyle = {
        fontSize: '14px',
        fontWeight: '400',
        width: '115px',
        textAlign: 'center',
    };

    const getAccountStatusConfig = (accountstatus) => {
        switch (String(accountstatus || '').toLowerCase().trim()) {
            case 'suspended':
            case 'inactive':
            case 'ghost':
            case 'incomplete profile':
                return {
                    background: '#D55F5A',
                    color: '#fff',
                };
            case 'hidden':
            case 'hidden by admin':
            case 'deactivated':
                return {
                    background: '#D9A300',
                    color: '#fff',
                };
            case 'approved':
            case 'active':
                return {
                    background: '#008E40',
                    color: '#fff',
                };
            default:
                return {
                    background: 'transparent',
                    color: '#000',
                };
        }
    };

    return (
        <>
            <div
                className="d-flex justify-content-between align-items-center"
                style={{
                    flexWrap: 'wrap',
                    gap: '0 20px',
                }}
            >
                <div className="d-flex gap-16 align-items-center">
                    <button
                        type="button"
                        onClick={handleBack}
                        style={{ background: 'transparent', border: 'none', padding: 0, display: 'inline-flex' }}
                    >
                        <span>
                            <LeftArrowIcons />
                        </span>
                    </button>

                    <div>
                        <div className="d-flex gap-10 text-black align-items-center fw-bold font-p font-36">
                            User List
                        </div>
                    </div>
                </div>
                <div>
                    <SearchFilter
                        genderFilter={genderFilter}
                        setGenderFilter={setGenderFilter}
                        setSearchTerm={setSearchTerm}
                    />
                </div>
            </div>

            <Box
                className="mb-16 d-flex justify-content-end align-items-center"
                sx={{
                    flexWrap: 'wrap',
                    gap: '0 20px',
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <form className="navbar-search">
                        <input
                            type="text"
                            name="search"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="icon">
                            <iconify-icon icon="ion:search-outline"></iconify-icon>
                        </span>
                    </form>

                    <div>
                        <Tooltip title="Export" arrow placement="top">
                            <button
                                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                                type="button"
                                onClick={handleExport}
                            >
                                <ExportIcon />
                            </button>
                        </Tooltip>
                    </div>

                    <div className="dropdown">
                        <Tooltip title="Manage Columns" arrow placement="top">
                            <button
                                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <ViewColumnOutlinedIcon />
                            </button>
                        </Tooltip>
                        <ul className="dropdown-menu manage-list">
                            {columnHeaders.map((column) => (
                                <li key={column.key} className="dropdown-item d-flex align-items-center gap-2">
                                    <input
                                        className="form-check-input"
                                        style={{ width: '18px', height: '18px' }}
                                        type="checkbox"
                                        checked={Boolean(visibleColumns[column.key])}
                                        onChange={() => handleColumnToggle(column.key)}
                                    />
                                    {column.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Box>

            <div className="table-scroll-container" style={{ overflow: 'visible !impotant' }}>
                <div 
                    ref={tableContainerRef}
                    className="custom-horizontal-scroll scroll-sm tablebtmdropnewclass"
                    onScroll={handleTableScroll}
                     style={{ 
    maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
    overflow: 'auto',
    position: 'relative'
  }}
                >
                    <table className="table bordered-table sm-table mb-0" style={{ position: 'relative' }}>
                        <thead  style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                                <th>#</th>
                                {visibleColumns.name && <th>Name</th>}
                                {visibleColumns.email && <th className="text-center">Email</th>}
                                {visibleColumns.age && <th className="text-center">Age</th>}
                                {visibleColumns.gender && <th className="text-center">Gender</th>}
                                {visibleColumns.location && <th className="text-center">Location</th>}
                                {visibleColumns.status && <th className="text-center">Subscription Status</th>}
                                {visibleColumns.date && <th className="text-center">Registration Date</th>}
                                {visibleColumns.session && <th className="text-center">Session</th>}
                                {visibleColumns.intrested && <th className="text-center">Intrested</th>}
                                {visibleColumns.profileview && <th className="text-center">Profile View</th>}
                                {visibleColumns.conversation && <th className="text-center">Conversation</th>}
                                {visibleColumns.duration && <th className="text-center">Duration</th>}
                                {visibleColumns.appinstalled && <th className="text-center">App Installed</th>}
                                {visibleColumns.verification && <th className="text-center">Verification</th>}
                                {visibleColumns.accountstatus && <th className="text-center">Account Status</th>}
                                {visibleColumns.reason && <th className="text-center">Reason</th>}
                                {visibleColumns.lastlogin && <th className="text-center">Last Login</th>}
                                {visibleColumns.flagreport && <th className="text-center">Flag Report By Users</th>}
                                {visibleColumns.blockedreport && <th className="text-center">Blocked By Users</th>}
                                {visibleColumns.activitylogs && <th className="text-center">Activity Logs</th>}
                                {visibleColumns.action && <th className="text-center">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading || campaignLoading ? (
                                <UserTableSkeleton
                                    rows={10}
                                    columns={Object.keys(visibleColumns).filter((key) => visibleColumns[key]).length + 1}
                                />
                            ) : userData.length > 0 ? (
                                userData.map((item, index) => (
                                    <tr key={item?._id || index}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>

                                        {visibleColumns.name && (
                                            <td>
                                                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <Box
                                                        sx={{ width: '35px', height: '35px', borderRadius: '100%' }}
                                                        component="img"
                                                        src={item.image}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            color: '#2E80DB',
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            fontFamily: 'Neue Montreal',
                                                        }}
                                                    >
                                                        {item.name}
                                                    </Typography>
                                                </Box>
                                            </td>
                                        )}
                                        {visibleColumns.email && <td className="text-center">{item.email || '-'}</td>}
                                        {visibleColumns.age && <td className="text-center">{item.age}</td>}
                                        {visibleColumns.gender && <td className="text-center">{item.gender}</td>}
                                        {visibleColumns.location && <td className="text-center">{item.location}</td>}
                                        {visibleColumns.status && (
                                            <td className="text-center">
                                                {(() => {
                                                    const config = getStatusConfig(item.status);
                                                    return (
                                                        <span
                                                            style={{
                                                                ...baseStatusStyle,
                                                                background: config.background,
                                                                color: config.color,
                                                                padding: config.background === 'transparent' ? '0' : baseStatusStyle.padding,
                                                            }}
                                                        >
                                                            {config.icon}
                                                            {item.status}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        )}
                                        {visibleColumns.date && <td className="text-center">{item.date}</td>}
                                        {visibleColumns.session && <td className="text-center">{item.session}</td>}
                                        {visibleColumns.intrested && (
                                            <td className="text-center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <HeartIconsUtm />
                                                    {item.intrested}
                                                </Box>
                                            </td>
                                        )}
                                        {visibleColumns.profileview && <td className="text-center">{item.profileview}</td>}
                                        {visibleColumns.conversation && <td className="text-center">{item.conversation}</td>}
                                        {visibleColumns.duration && <td className="text-center">{item.duration}</td>}
                                        {visibleColumns.appinstalled && (
                                            <td className="text-center">
                                                {(() => {
                                                    const config = getAppConfig(item.appinstalled);
                                                    return (
                                                        <span
                                                            style={{
                                                                ...baseAppStyle,
                                                                background: config.background,
                                                                color: config.color,
                                                                padding: config.background === 'transparent' ? '0' : baseAppStyle.padding,
                                                            }}
                                                        >
                                                            {item.appinstalled}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        )}
                                        {visibleColumns.verification && (
                                            <td className="text-center">
                                                {(() => {
                                                    const config = getVerificationConfig(item.verification);
                                                    return (
                                                        <span
                                                            style={{
                                                                ...baseVerificationStyle,
                                                                color: config.color,
                                                                padding: config.background === 'transparent' ? '0' : baseVerificationStyle.padding,
                                                            }}
                                                        >
                                                            {config.icon}
                                                            {item.verification}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        )}
                                        {visibleColumns.accountstatus && (
                                            <td
                                                className="text-center"
                                                style={{
                                                    ...baseAccountStatusStyle,
                                                    ...getAccountStatusConfig(item.accountstatus),
                                                }}
                                            >
                                                {item.accountstatus}
                                            </td>
                                        )}
                                        {visibleColumns.reason && (
                                            <td className="text-center">{item.reason}</td>
                                        )}
                                        {visibleColumns.lastlogin && (
                                            <td className="text-center">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: '14px',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: '6px',
                                                            alignItems: 'center',
                                                            width: '80%',
                                                        }}
                                                    >
                                                        {item.isOnline && (
                                                            <span
                                                                style={{
                                                                    background: '#008E40',
                                                                    width: '11px',
                                                                    height: '11px',
                                                                    borderRadius: '100%',
                                                                }}
                                                            ></span>
                                                        )}
                                                        {item.lastlogin}
                                                    </Box>

                                                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                        {item.loginDevices?.includes('desktop') && <DesktopLoginIcon />}
                                                        {item.loginDevices?.includes('mobile') && <MobileLoginIcon />}
                                                    </Box>
                                                </Box>
                                            </td>
                                        )}
                                        {visibleColumns.flagreport && (
                                            <td className="text-center">
                                                <Box
                                                    sx={{
                                                        border: '1px solid #ddd',
                                                        borderRadius: '5px',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '3px 6px',
                                                        height: '30px',
                                                        width: '70px',
                                                    }}
                                                >
                                                    <FlagIcons />
                                                    <span style={{ textDecoration: 'underline' }}>{item.flagreport}</span>
                                                </Box>
                                            </td>
                                        )}
                                        {visibleColumns.blockedreport && (
                                            <td className="text-center">
                                                <Box
                                                    sx={{
                                                        border: '1px solid #ddd',
                                                        borderRadius: '5px',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        padding: '3px 6px',
                                                        height: '30px',
                                                        width: '70px',
                                                    }}
                                                >
                                                    <span style={{ textDecoration: 'underline' }}>{item.blockedreport}</span>
                                                </Box>
                                            </td>
                                        )}
                                        {visibleColumns.activitylogs && (
                                            <td
                                                className="text-center"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleOpenLogs(item)}
                                            >
                                                <span>
                                                    <ActivityLogsIcons />
                                                </span>
                                            </td>
                                        )}
                                        {visibleColumns.action && (
                                            <td className="text-center">
                                                <div className="dropdown">
                                                    <button
                                                        className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon text-sm"
                                                        type="button"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                    >
                                                        Action
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                        <li>
                                                            <a
                                                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                                                onClick={() => handleOpenSuspend(item)}
                                                            >
                                                                {item?.suspend ? 'Lift Suspension' : 'Suspend'}
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a
                                                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                                                onClick={() => handleOpenProfile(item)}
                                                            >
                                                                View
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : hasLoadedUsers ? (
                                <tr>
                                    <td colSpan="20" className="text-center py-3">
                                        No data found
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>

                {scrollState.scrollWidth > scrollState.clientWidth && (
                    <div className="sticky-scroll-container">
                        <div className="sticky-scroll-track" onClick={handleStickyScroll}>
                            <div
                                ref={scrollThumbRef}
                                className="sticky-scroll-thumb"
                                style={{
                                    width: `${thumbWidth}px`,
                                    transform: `translateX(${thumbPosition}px)`,
                                }}
                                onMouseDown={handleThumbDrag}
                            />
                        </div>
                    </div>
                )}
            </div>

            {!loading && Boolean(totalPages) && (
                <div className="d-flex justify-content-center mt-16">
                    <Pagination count={totalPages} page={currentPage} onChange={handlePageChange}  sx={{
    "& .MuiPaginationItem-root": {
      color: "#000",  
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important",  
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }}  />
                </div>
            )}

            <Modal open={openSuspend} onClose={() => setOpenSuspend(false)}>
                <Box sx={style}>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                        }}
                        onClick={() => setOpenSuspend(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                    <SuspendCard
                        userId={selectedUser?._id}
                        suspend={selectedUser?.suspend}
                        handleClose={() => setOpenSuspend(false)}
                        fetchData={fetchData}
                    />
                </Box>
            </Modal>

            <Modal open={openLogs} onClose={() => setOpenLogs(false)}>
                <Box sx={styleUser}>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                        }}
                        onClick={() => setOpenLogs(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                    <ActivityLogs userId={selectedUser?._id} data={selectedUser} />
                </Box>
            </Modal>

            <Modal open={openProfile} onClose={() => setOpenProfile(false)}>
                <Box sx={styleProfile}>
                    <Box
                        sx={{
                            position: 'sticky',
                            top: '0px',
                            backgroundColor: 'white',
                            zIndex: 10,
                            padding: '10px',
                            borderBottom: 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 10,
                                right: 0,
                            }}
                            onClick={() => setOpenProfile(false)}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <UserProfileMainPage userId={selectedUser?._id} />
                </Box>
            </Modal>
        </>
    );
};

export default UserListCampaignsPage;
